import { globals } from 'config/globals';
import { Controller } from 'core/Controller';
import { ScrollbarController } from 'app/chrome/scrollbar/ScrollbarController';
import { Events } from 'core/events/Events';
import { AppEvents } from 'app/events/AppEvents';
import { MenuStateType, SectionType } from 'app/data/Enums';
import { HistoryStateModel } from 'app/model/HistoryStateModel';
import { UIThemeChangeEvent } from 'app/events/UIThemeChangeEvent';
import { UIThemeColourType } from 'app/data/Enums';
import ProjectHeaderSizes from 'app/views/project/ProjectHeaderSizes';
import type { MouseController } from 'app/chrome/mouse/MouseController';
import SiteTransitions from 'app/services/SiteTransitions';


export class UIController extends Controller {
	private _$section: HTMLElement;
	private _$prompt: HTMLElement;
	private _animateInSubsection: boolean = false;
	private _uiShowing: boolean = true;
	private _touchCounter: number = 0;
	private _scrollBackframes: number = 0;

	private _scrollbarController: ScrollbarController;
	private _mouseController: MouseController;
	private _state: HistoryStateModel;

	private _scrollDiff = 0;
	private _lastScroll = 0;
	private _verticalDragPointerDown: boolean = false;
	private _aboutDeepLink: boolean = false;

	private _$body: HTMLElement;
	private _showUiTimeout;

	private _uiTargets: any = {
		info: null,
		logo: null,
		projects: null,
		backToHome: null
	};

	public init(): void {
		Events.on(UIThemeChangeEvent, this._onThemeChange.bind(this));
		document.querySelector('.ui__logo');
		document.querySelector('.ui__hamburger');
		this._$prompt = document.querySelector('.drag-prompt');

		this._$body = document.querySelector('body');

		this._uiTargets.logo = document.querySelector('.ui__logo');
		this._uiTargets.projects = document.querySelector('.show__projects__button');
		this._uiTargets.info = document.querySelector('.show__info__button');
		this._uiTargets.backToHome = document.querySelector('.show__home__button');
	}

	public setControllers(scrollbar: ScrollbarController, mouse: MouseController): void {
		this._scrollbarController = scrollbar;
		this._mouseController = mouse;
	}

	public dark(): void {
		let event: UIThemeChangeEvent = new UIThemeChangeEvent({
			theme: UIThemeColourType.DARK
		});
		this._onThemeChange(event);
	}

	public light(): void {
		let event: UIThemeChangeEvent = new UIThemeChangeEvent({
			theme: UIThemeColourType.LIGHT
		});
		this._onThemeChange(event);
	}

	public DOMReady(): void {
		this._$section = this.container.querySelector('.-section');
		this.container.querySelector('.-subsection');

		Events.on('click', '.ui__breadcrumb', (e: MouseEvent) => {
			e.preventDefault();
			this._onBreadcrumbClick(e);
			return false;
		});

		Events.on('click', '.ui__logo__link', (e: MouseEvent) => {
			if (globals.tmpApp) {
				e.preventDefault();
				return false;
			}

			let hasBeenTriggeredByMenu = this.get('navmenu').debugLogoPress();
			e.preventDefault();

			if (!hasBeenTriggeredByMenu) {
				Events.dispatch(AppEvents.DEBUG_LOGO_PRESS);
			}

			return false;
		});

		Events.on('mouseover', '.ui__logo__link', (e: MouseEvent) => {
			if (this._state.section != SectionType.WORK) {
				return;
			}

			this.DOM.addClass(SiteTransitions.$body, 'work-back-hover');
		});

		Events.on('mouseout', '.ui__logo__link', (e: MouseEvent) => {
			if (this._state.section != SectionType.WORK) {
				return;
			}

			this.DOM.removeClass(SiteTransitions.$body, 'work-back-hover');
		});

		document.addEventListener(
			'touchmove',
			(e) => {
				this._scrollDiff = 0;
				this._touchCounter = 0;
				this._lastScroll = this._scrollbarController.getScroll();
			},
			{ passive: true }
		);

		document.addEventListener(
			'touchend',
			(e) => {
				this._verticalDragPointerDown = false;
				this._scrollDiff = 0;
				this._lastScroll = this._scrollbarController.getScroll();
				this._touchCounter = 0;
			},
			{ passive: true }
		);

		document.addEventListener(
			'touchstart',
			(e) => {
				this._verticalDragPointerDown = true;

				this._scrollDiff = 0;
				this._touchCounter = 0;
				this._lastScroll = this._scrollbarController.getScroll();
			},
			{ passive: true }
		);

		if (this._state) {
			this.setState(this._state);
		}
	}

	public showUi(delayMs = 0) {
		clearTimeout(this._showUiTimeout);

		if (!this._$body) {
			this._$body = document.querySelector('body');
		}

		if (delayMs === 0) {
			this.DOM.addClass(this._$body, 'ui-show');
			return;
		}

		this._showUiTimeout = setTimeout(() => {
			this.DOM.addClass(this._$body, 'ui-show');
		}, delayMs);
	}

	public hideUi(delayMs = 0) {
		clearTimeout(this._showUiTimeout);

		if (delayMs == 0) {
			this.DOM.removeClass(this._$body, 'ui-show');
			return;
		}

		this._showUiTimeout = setTimeout(() => {
			this.DOM.removeClass(this._$body, 'ui-show');
		}, delayMs);
	}

	public showUiItem(item: string) {
		this.DOM.addClass(this._uiTargets[item], 'ui-can-show');
	}

	public hideUiItem(item: string) {
		this.DOM.removeClass(this._uiTargets[item], 'ui-can-show');
	}

	public showNextPrompt(type: string) {
		this.clearPromptClasses();
		this.DOM.addClass(this._$body, 'drag-prompt-active');
		this.DOM.addClass(this._$prompt, '-next-prompt');
		this.DOM.addClass(this._$prompt, '-next-' + type);
	}

	private clearPromptClasses() {
		this.DOM.removeClass(this._$prompt, '-next-prompt');
		this.DOM.removeClass(this._$prompt, '-next-spaces');
		this.DOM.removeClass(this._$prompt, '-next-systems');

		this.DOM.removeClass(this._$prompt, '-right-hint');
		this.DOM.removeClass(this._$prompt, '-left-hint');
	}

	public showDragPrompt(progress = 0) {
		this.clearPromptClasses();

		console.trace('Show drag prompt -----');
		let text = this._mouseController.lastPointerWasTouch() ? 'Swipe to explore' : 'Drag to explore';
		this.DOM.setHTML(this._$prompt.querySelector('.drag-prompt-label'), text);
		this.DOM.addClass(this._$body, 'drag-prompt-active');

		if (progress < 0.9) this.DOM.addClass(this._$prompt, '-right-hint');
		if (progress > 0.1) this.DOM.addClass(this._$prompt, '-left-hint');
	}

	public resetDragPrompt() {
		this.DOM.removeClass(this._$body, 'drag-prompt-active');
	}

	public hideDragPrompt() {
		this.resetDragPrompt();
		setTimeout(() => {
			this._mouseController.guideFollowActive = false;
			this.DOM.addClass(this._$body, '-prompt-no-follow-cursor');
		}, 800);
	}

	public setAboutDeeplink() {
		this._aboutDeepLink = true;
	}

	public hideForProject(): void {
	}

	public showBackFromProject(): void {
		this.light();
	}

	//Monitor scroll dragging
	public setVerticalDragPointerDown(isDown: boolean): void {
		this._verticalDragPointerDown = isDown;
		this._scrollDiff = 0;
		this._lastScroll = this._scrollbarController.getScroll();
	}

	public setNavState(state: MenuStateType): void {
		if (state === MenuStateType.OPEN) {
			this.DOM.addClass(this.container, '-navopen');
			return;
		}

		this.DOM.removeClass(this.container, '-navopen');
	}

	public setIntialState(state: HistoryStateModel): void {
		this.DOM.addClass(this._$body, `-deeplinksection-${state.section}`);
		this.setState(state);
	}

	public unpause(): void {
		this._setUIVisiblity(true);
	}

	/** Section has change so set state */
	public setState(state: HistoryStateModel): void {
		this._state = state;

		if (!this._$section) {
			return;
		}

		let subSectionName: string = null;

		//If state is not home then it should have a breadcrumb showing
		if (state.section === SectionType.PROJECT) {
			subSectionName = (state.projectSlug.charAt(0).toUpperCase() + state.projectSlug.substr(1)).replace(
				new RegExp('-', 'g'),
				' '
			);
		}

		//Set the 2nd subsection breadcrumb:
		if (
			!subSectionName &&
			state.subsection &&
			state.section !== SectionType.ABOUT &&
			state.section !== SectionType.WORK
		) {
			subSectionName = state.subsection.charAt(0).toUpperCase() + state.subsection.substr(1);
		}

		this._setHomeClassState();
	}


	/** set the -home class if on home */
	private _setHomeClassState(): void {
		if (this._state.section === SectionType.HOME) {
			this.DOM.addClass(this.container, '-home');
			return;
		}

		this.DOM.removeClass(this.container, '-home');
	}

	public tick(): void {
		if (this._scrollDiff > 0) {
			this._scrollDiff -= 5 + this._touchCounter * 5;
		}

		if (this._touchCounter <= 1) {
		}

		if (this._animateInSubsection) {
		}


		if (
			this._scrollbarController &&
			!this._scrollbarController.endFlagged &&
			this._scrollbarController.getProgress() > 0.95
		) {
			this._scrollbarController.endFlagged = true;
		}
	}

	public show(): void {
		if (this._aboutDeepLink) {
			return;
		}



		this.setState(this._state);
	}

	public hide(): void {
	}

	/** Update scroll */
	public scroll(scrollY: number): void {
		//Need to test if the UI should be hidden (when scrolling)

		if (this._state.section === SectionType.ABOUT) {
			return;
		}

		this._testUIVisiblity();
	}

	//PRIVATE
	//----------------------------------------------------------------------------
	//----------------------------------------------------------------------------
	//----------------------------------------------------------------------------
	//----------------------------------------------------------------------------

	/** Logo area breadcrumb click */
	private _onBreadcrumbClick(e: MouseEvent): void {
		if (!this.DOM.hasClass(e.target as Element, '-active')) {
			return;
		}
		Events.dispatch(AppEvents.CLOSE_PROJECT);
	}

	/** On theme change */
	private _onThemeChange(event: UIThemeChangeEvent): void {
		//TODO - move 'project close logic from this class'
		if (event.theme === UIThemeColourType.DARK) {
			this.DOM.addClass(this.container, '-dark');
			this.DOM.addClass(document.querySelector('.project__close'), '-dark');
			this.DOM.addClass(document.querySelector('.project__home'), '-dark');
			return;
		}

		this.DOM.removeClass(this.container, '-dark');
		this.DOM.removeClass(document.querySelector('.project__close'), '-dark');
		this.DOM.removeClass(document.querySelector('.project__home'), '-dark');
	}

	/** Check if the UI should be shown / hidden based on scroll state */
	private _testUIVisiblity(): void {
		let scrollY: number = this._scrollbarController.getRealScroll();

		let scrollDirection: number = this._scrollbarController.getDirection();

		this._scrollBackframes += scrollDirection;
		if (this._scrollBackframes > 10) this._scrollBackframes = 10;
		else if (this._scrollBackframes < 0) this._scrollBackframes = 0;

		//1.) If showing then check:
		//+ If last direction was down (+1) then
		//- If scroll is moved from top and the
		if (this._uiShowing && scrollDirection === 1) {

			if (this._testUIHideFromScrollPos(scrollY)) {
				this._setUIVisiblity(false);
				this._scrollDiff = 0;
			}

			this._lastScroll = scrollY;
			return;
		}

		//2.) If not showing then check
		//- If direction is going back up then show
		//- If outside hide zone on scroller then show
		//- If scrolling is not active then show
		//- If navMenu is open then then show

		if (!this._uiShowing) {
			let isScrollActive: boolean = this._scrollbarController.getActive();


			if (!isScrollActive) {
				this._setUIVisiblity(true);
				this._scrollDiff = 0;
				this._lastScroll = scrollY;
				return;
			}

			if (scrollY < 10) {
				this._scrollDiff = 0;
				this._setUIVisiblity(true);
				this._lastScroll = scrollY;
				return;
			}

			if (scrollDirection === -1 || !this._testUIHideFromScrollPos(scrollY)) {
				this._scrollDiff += this._lastScroll - scrollY;

				if ((!this._verticalDragPointerDown && this._scrollDiff >= 70 + this._touchCounter * 30) || scrollY < 10) {

					this._scrollDiff = 0;
					this._setUIVisiblity(true);
				}
			}

			this._lastScroll = scrollY;

			return;
		}
	}

	/** SHow / hide all the UI that is triggered by */
	//TODO remove Project concerns like close / home button (Or move these UI controller!!)
	private _setUIVisiblity(show: boolean): void {
		return;
		this._uiShowing = show;

		if (this._uiShowing) {
			this.DOM.removeClass(this.container, '-hide');
			this.DOM.addClass(document.querySelector('.project__close'), '-show');
			this.DOM.addClass(document.querySelector('.project__homebutton'), '-show');
			this.DOM.addClass(document.querySelector('#info-paging'), '-scrollshow');
			this.DOM.addClass(document.querySelector('.project'), '-uivisible');
			return;
		}

		//The UI is hidden so flag this:
		this.DOM.removeClass(document.querySelector('.project__close'), '-show');
		this.DOM.removeClass(document.querySelector('.project__homebutton'), '-show');
		this.DOM.removeClass(document.querySelector('#info-paging'), '-scrollshow');
		this.DOM.removeClass(document.querySelector('.project'), '-uivisible');

		this.DOM.addClass(this.container, '-hide');
	}

	/** Test if scroller is 'hide' zone for the UI elements */
	private _testUIHideFromScrollPos(scrollY: number): boolean {
		return scrollY > 10;

		let scrollRatio: number = this._scrollbarController.getProgress();
		return scrollY > ProjectHeaderSizes.headerHeight() - 50 && scrollRatio < 0.99;
	}
}
